import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Check, Loader2, Search, Snowflake } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useCreateStorageNode } from '@/hooks/useStorage'
import { useEquipmentUnits } from '@/hooks/useEquipment'
import { sanitizeCode } from '@/components/inventory/storage/LayoutControls'
import { useSetEquipmentRoom } from '@/hooks/useFacility'
import { usePermission } from '@/lib/permissions'
import { PlacePicker } from '@/components/equipment/facility/PlacePicker'
import { FacilityStudio } from '@/components/equipment/facility/FacilityStudio'

/**
 * Turn a machine into a storage unit.
 *
 * Guided rather than a bare dropdown because two things are non-obvious and
 * both used to be discovered by failure: only equipment whose TYPE is
 * Purpose = Storage can hold stock, and rooting a unit creates an empty tree
 * that still needs a layout before anything can go in it. The dialog says both,
 * and hands straight over to the setup wizard.
 *
 * The picker is the Receive-stock list rather than a `<Select>`: the equipment
 * ID is what is painted on the machine and what the code is derived from, so it
 * has to be the thing you read — a dropdown line put it beside the type in the
 * same weight and made the two hard to tell apart.
 */
export function AddStorageUnitDialog({
  open,
  onClose,
  rootedEquipmentIds,
  onAdded,
}: {
  open: boolean
  onClose: () => void
  /** Units that already root a tree — offering one again would 409. */
  rootedEquipmentIds: Set<string | null>
  /** Called with the new root's id, so the caller can open the wizard. */
  onAdded: (rootId: string) => void
}) {
  const units = useEquipmentUnits()
  const createNode = useCreateStorageNode()
  const setRoom = useSetEquipmentRoom()
  const { can } = usePermission()
  const canPlace = can('equipment.manage')
  const [pick, setPick] = useState('')
  const [query, setQuery] = useState('')
  // Where the machine stands (z8nrz7fw25), asked here because this is when
  // someone is setting the unit up. Starts from the room it is already in.
  const [roomId, setRoomId] = useState<string | null>(null)
  const [facilitySetupOpen, setFacilitySetupOpen] = useState(false)
  // Two steps: which machine, then where it stands. The second has the room to
  // show the site as a picture rather than squeezing a picker under the list.
  const [step, setStep] = useState<'machine' | 'place'>('machine')

  useEffect(() => {
    if (open) {
      setPick('')
      setQuery('')
      setRoomId(null)
      setStep('machine')
    }
  }, [open])

  const all = units.data ?? []
  const storageUnits = useMemo(
    () => all.filter((u) => u.equipmentType?.purpose === 'STORAGE'),
    [all],
  )
  const eligible = useMemo(
    () => storageUnits.filter((u) => !rootedEquipmentIds.has(u.id)),
    [storageUnits, rootedEquipmentIds],
  )
  const chosen = eligible.find((u) => u.id === pick) ?? null
  useEffect(() => setRoomId(chosen?.roomId ?? null), [chosen?.id, chosen?.roomId])

  // The room first, then the unit: a refused room (a transfer running on the
  // machine) stops before anything is created, rather than leaving a unit
  // behind that is somewhere it was not meant to be.
  const add = async () => {
    if (!chosen) return
    if (canPlace && roomId !== (chosen.roomId ?? null)) {
      await setRoom.mutateAsync({ equipmentId: chosen.id, roomId })
    }
    const node = await createNode.mutateAsync({ kind: 'UNIT', code: sanitizeCode(chosen.label), equipmentId: chosen.id })
    onAdded(node.id)
  }
  const pending = createNode.isPending || setRoom.isPending

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return eligible
    return eligible.filter(
      (u) =>
        u.label.toLowerCase().includes(q) ||
        (u.equipmentType?.name ?? '').toLowerCase().includes(q) ||
        (u.locationSite ?? '').toLowerCase().includes(q),
    )
  }, [eligible, query])

  // Grouped under the equipment type, matching the Receive-stock picker: a long
  // fleet reads as sections rather than one undifferentiated list.
  const sections = useMemo(() => {
    const byType = new Map<string, typeof filtered>()
    for (const u of filtered) {
      const key = u.equipmentType?.name || 'Other'
      const list = byType.get(key) ?? []
      list.push(u)
      byType.set(key, list)
    }
    return [...byType.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, list]) => ({
        name,
        items: [...list].sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { numeric: true }),
        ),
      }))
  }, [filtered])

  const empty = eligible.length === 0

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          createNode.reset()
          onClose()
        }
      }}
    >
      <DialogContent className={cn('flex max-h-[85vh] flex-col gap-0 p-0', step === 'place' ? 'sm:max-w-4xl' : 'sm:max-w-2xl')}>
        <DialogHeader className='border-b border-gray-100 px-6 pb-4 pt-5'>
          <DialogTitle className='flex items-center gap-2'>
            <Snowflake className='h-5 w-5 text-sky-600' />
            Add a storage unit
          </DialogTitle>
          <DialogDescription>
            {step === 'place' && chosen
              ? `Where does ${chosen.label} stand? Walk the site down to its room.`
              : 'Pick the machine that holds stock. You will describe what is inside it next.'}
          </DialogDescription>
          {canPlace && !empty && (
            <ol className='flex items-center gap-2 pt-2 text-xs' aria-label='Steps'>
              {(['machine', 'place'] as const).map((s, i) => (
                <li key={s} className='flex items-center gap-2'>
                  {i > 0 && <span className='h-px w-6 bg-slate-300' aria-hidden />}
                  <span
                    className={cn(
                      'flex items-center gap-1.5 rounded-full px-2.5 py-0.5',
                      step === s ? 'bg-primary text-primary-foreground' : 'bg-slate-100 text-slate-600',
                    )}
                    aria-current={step === s ? 'step' : undefined}
                  >
                    <span className='font-semibold'>{i + 1}</span> {s === 'machine' ? 'Machine' : 'Where it is'}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </DialogHeader>

        {step === 'place' && chosen ? (
          <div className='min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-sm'>
                <span className='font-mono font-semibold'>{chosen.label}</span>
                <span className='text-muted-foreground'> · {chosen.equipmentType?.name}</span>
              </p>
              <button type='button' className='text-xs font-medium text-primary hover:underline' onClick={() => setFacilitySetupOpen(true)}>
                Set up facility
              </button>
            </div>
            <PlacePicker value={roomId} onChange={setRoomId} disabled={pending} />
          </div>
        ) : units.isLoading ? (
          <p className='px-6 py-10 text-center text-sm text-muted-foreground'>
            <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
            Loading equipment…
          </p>
        ) : empty ? (
          // Two different empty states. "None left" and "none exist" need
          // different actions, and one message for both sends people looking in
          // the wrong place.
          <div className='space-y-2 px-6 py-8 text-sm'>
            {storageUnits.length === 0 ? (
              <>
                <p className='font-medium'>No equipment can hold stock yet.</p>
                <p className='text-muted-foreground'>
                  Only a machine whose <span className='font-medium'>type</span> is set to
                  Purpose = Storage can. Add or edit an equipment type on the Equipment Type tab,
                  then come back.
                </p>
              </>
            ) : (
              <>
                <p className='font-medium'>Every storage machine already has a layout.</p>
                <p className='text-muted-foreground'>
                  All {storageUnits.length} of them are set up. Open one from the table to change
                  what is inside it.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className='border-b border-gray-100 px-6 py-3'>
              <div className='relative'>
                <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='Search by equipment ID, type or site…'
                  className='h-9 pl-8'
                />
              </div>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto px-6 py-3'>
              {sections.length === 0 && (
                <p className='py-8 text-center text-sm text-muted-foreground'>
                  Nothing matches “{query}”.
                </p>
              )}
              {sections.map((section) => (
                <div key={section.name} className='mb-3'>
                  <p className='sticky top-0 z-10 -mx-1 bg-background px-1 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                    {section.name}
                    <span className='ml-1.5 font-normal normal-case text-gray-300'>
                      ({section.items.length})
                    </span>
                  </p>
                  <ul className='space-y-1'>
                    {section.items.map((u) => {
                      const selected = u.id === pick
                      return (
                        <li key={u.id}>
                          <button
                            type='button'
                            onClick={() => setPick(u.id)}
                            className={cn(
                              'group flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                              selected
                                ? 'border-primary bg-primary/5'
                                : 'hover:border-primary/40 hover:bg-primary/5',
                            )}
                          >
                            <Snowflake
                              className={cn(
                                'h-4 w-4 shrink-0',
                                selected ? 'text-primary' : 'text-muted-foreground',
                              )}
                            />
                            <div className='min-w-0 flex-1'>
                              {/* The equipment ID leads: it is what is on the
                                  machine, and what the storage code comes from. */}
                              <div className='font-mono text-sm font-semibold'>{u.label}</div>
                              <div className='mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground'>
                                <span>{u.equipmentType?.name}</span>
                                {u.locationSite && <span>· {u.locationSite}</span>}
                                {u.serial && u.serial !== '—' && <span>· S/N {u.serial}</span>}
                              </div>
                            </div>
                            {selected && <Check className='h-4 w-4 shrink-0 text-primary' />}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}

        <div className='space-y-2 border-t border-gray-100 px-6 py-3'>
          {chosen && (step === 'place' || !canPlace) && (
            <p className='text-xs text-muted-foreground'>
              Creates an empty storage unit called{' '}
              <span className='font-mono font-medium text-foreground'>
                {sanitizeCode(chosen.label)}
              </span>
              . Nothing can be stored in it until you describe what it holds — that step opens
              straight after.
            </p>
          )}
          {(setRoom.error ?? createNode.error) && (
            <p className='text-sm text-destructive'>{String((setRoom.error ?? createNode.error)?.message)}</p>
          )}
          <div className='flex justify-end gap-2'>
            {step === 'place' ? (
              <Button variant='outline' className='mr-auto' onClick={() => setStep('machine')} disabled={pending}>
                Back
              </Button>
            ) : null}
            <Button variant='outline' onClick={() => { createNode.reset(); onClose() }}>
              {empty ? 'Close' : 'Cancel'}
            </Button>
            {!empty && step === 'machine' && canPlace ? (
              <Button className='gap-1.5' disabled={!chosen || !sanitizeCode(chosen?.label ?? '')} onClick={() => setStep('place')}>
                Next: where it is
                <ArrowRight className='h-4 w-4' />
              </Button>
            ) : !empty && (
              <Button
                className='gap-1.5'
                disabled={!chosen || pending || !sanitizeCode(chosen.label)}
                // Errors land in the mutations and are shown above.
                onClick={() => void add().catch(() => undefined)}
              >
                {pending ? 'Adding…' : 'Add and set up'}
                <ArrowRight className='h-4 w-4' />
              </Button>
            )}
          </div>
        </div>
        {/* Facility, buildings, floors and rooms: set up once, then picked above.
            Inside the content so the outer dialog's focus trap lets it work. */}
        <FacilityStudio open={facilitySetupOpen} onOpenChange={setFacilitySetupOpen} />
      </DialogContent>
    </Dialog>
  )
}
